import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Fragment, useState } from "react";
import { useFinance } from "./FinanceContext";
import { clone } from "./data/model";
import { PERM_CATALOG } from "./permissions";

const MODULE_MATRIX: [string, string, string, string, string, string][] = [
  ["Dashboard", "✔", "✔", "✔", "✔", "View"],
  ["Budget Planning", "✔", "✔", "✔", "✔", "View"],
  ["Budget vs Actual", "✔", "✔", "✔", "✔", "View"],
  ["Revenue", "✔", "✔", "✔", "View", "View"],
  ["Reports", "✔", "✔", "✔", "✔", "View"],
  ["Tally Upload", "✔", "✔", "✔", "✖", "✖"],
  ["Validation", "✔", "✔", "✔", "✖", "✖"],
  ["Approval", "✔", "✔", "✔", "✖", "✖"],
  ["Freeze (M/Q/H/Y)", "✔", "✔", "✔", "✖", "✖"],
  ["Version Management", "✔", "✔", "✔", "View", "View"],
  ["User Management", "✔", "✔", "✖", "✖", "✖"],
  ["System Settings", "✔", "✔", "✖", "✖", "✖"],
];

export function RolesPermissionsPage() {
  const { roles, setRoles, rolePerms, setRolePerms, has, logAudit } = useFinance();
  const canEdit = has("roles.manage");
  const [newRole, setNewRole] = useState("");

  function toggle(r: string, p: string) {
    if (!canEdit || r === "CEO") return;
    setRolePerms((prev) => {
      const n = clone(prev);
      const arr = n[r] ?? [];
      const i = arr.indexOf(p);
      if (i >= 0) arr.splice(i, 1);
      else arr.push(p);
      n[r] = arr;
      return n;
    });
    logAudit("Permission changed", `${r} · ${p}`);
  }

  function addRole() {
    const nm = newRole.trim();
    if (!nm || roles.includes(nm)) return;
    setRoles((rs) => [...rs, nm]);
    setRolePerms((prev) => ({ ...prev, [nm]: ["dashboard.view", "reports.view"] }));
    logAudit("Role created", `${nm} (configure permissions)`);
    setNewRole("");
  }

  const groups = [...new Set(PERM_CATALOG.map((p) => p.g))];

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Roles &amp; Permissions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        RBAC + PBAC · toggle granular permissions per role · add new roles without code changes{" "}
        {canEdit ? "" : "(view only — CEO manages)"}
      </Typography>

      {!canEdit ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Edits here are demo/in-memory only (this page does not persist to a backend endpoint) and
          are reset on reload.
        </Alert>
      ) : null}

      {canEdit ? (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <TextField
            size="small"
            label="New role name"
            placeholder="e.g. Internal Auditor"
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
          />
          <Button size="small" variant="contained" onClick={addRole}>
            + Add Role
          </Button>
          <Typography variant="body2" color="text.secondary">
            New roles are pure config — no code change needed.
          </Typography>
        </Stack>
      ) : null}

      <TableContainer sx={{ maxHeight: "60vh", mb: 3 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Permission</TableCell>
              {roles.map((r) => (
                <TableCell key={r} align="center">
                  {r}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((g) => (
              <Fragment key={g}>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell
                    colSpan={roles.length + 1}
                    sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11 }}
                  >
                    {g}
                  </TableCell>
                </TableRow>
                {PERM_CATALOG.filter((p) => p.g === g).map((p) => (
                  <TableRow key={p.k}>
                    <TableCell>{p.l}</TableCell>
                    {roles.map((r) => {
                      const on = r === "CEO" || (rolePerms[r] ?? []).indexOf(p.k) >= 0;
                      return (
                        <TableCell key={r} align="center">
                          <Checkbox
                            size="small"
                            checked={on}
                            disabled={!canEdit || r === "CEO"}
                            onChange={() => toggle(r, p.k)}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Module Permission Matrix (reference)
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Module</TableCell>
                <TableCell align="center">CEO</TableCell>
                <TableCell align="center">Director</TableCell>
                <TableCell align="center">Finance Head</TableCell>
                <TableCell align="center">Finance Team</TableCell>
                <TableCell align="center">Viewer</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {MODULE_MATRIX.map((row) => (
                <TableRow key={row[0]}>
                  <TableCell>{row[0]}</TableCell>
                  {row.slice(1).map((x, j) => (
                    <TableCell
                      key={j}
                      align="center"
                      sx={{
                        color:
                          x === "✔" ? "success.main" : x === "✖" ? "error.main" : "text.secondary",
                        fontWeight: 700,
                      }}
                    >
                      {x}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
